import { useMemo } from 'react'

import { alpha } from '@mui/material/styles'
import { ChartData, ChartOptions } from 'chart.js'
import { Chart } from 'react-chartjs-2'

import { SUPPLIER_ACTIVITY_RECENCY_CHART_HEIGHT } from 'components/reports/Metrics/constants/SupplierActivityRecency'
import { SupplierActivityRecencyPointDto } from 'generated/graphql'
import {
  LABEL_PADDING_TOP,
  recencyPointValueLabels,
} from 'helpers/supplierActivityRecencyChart.helpers'

import { LINE_STYLE } from '../components/chartConfig'

interface Props {
  points: SupplierActivityRecencyPointDto[]
  color: string
  xAxisLabel: string
  height?: number
}

export const RecencyCumulativeChart = ({
  points,
  color,
  xAxisLabel,
  height = SUPPLIER_ACTIVITY_RECENCY_CHART_HEIGHT,
}: Props) => {
  const data = useMemo<ChartData<'line', number[], string>>(
    () => ({
      labels: points.map((point) => (point.day === 0 ? 'Today (0)' : `${point.day}`)),
      datasets: [
        {
          ...LINE_STYLE,
          type: 'line' as const,
          label: 'Cumulative % of suppliers',
          data: points.map((point) => point.percentage),
          borderColor: color,
          backgroundColor: alpha(color, 0.15),
          pointBackgroundColor: color,
          fill: true,
          supplierCounts: points.map((point) => point.supplierCount),
        },
      ],
    }),
    [points, color],
  )

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: LABEL_PADDING_TOP } },
      scales: {
        x: {
          grid: { display: false },
          title: { display: true, text: xAxisLabel },
          ticks: { maxRotation: 0, autoSkip: false },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: 'Cumulative % of suppliers' },
          ticks: { callback: (value) => `${value}%` },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              const point = points[context.dataIndex]

              return `${point.percentage}% · ${point.supplierCount.toLocaleString()} supplier(s)`
            },
          },
        },
      },
    }),
    [points, xAxisLabel],
  )

  return (
    <div style={{ height, position: 'relative' }}>
      <Chart<'line', number[], string>
        type='line'
        data={data}
        options={options}
        plugins={[recencyPointValueLabels]}
      />
    </div>
  )
}
